import { instanceToPlain, plainToInstance } from 'class-transformer';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { Collections, db, functions } from '../../utils/firebase.config';
import EntityCondominium from '../../entities/condominium.entity';
import { converterOf } from '../../utils/firebase.utils';
import FetchError from '../../errors/fetch.error';
import CreateCondominiumInput from './resources/create-condominium.resource';
import UpdateCondominiumInput from './resources/update-condominium.resource';

export function useCondominiums() {
  const condominiumsCollection = collection(db, Collections.Condominiums).withConverter(converterOf(EntityCondominium));

  const fetchAll = async (): Promise<EntityCondominium[]> => {
    const response = await getDocs<EntityCondominium>(condominiumsCollection);

    return response.docs.map((cond) => cond.data());
  };

  const fetchById = async (id: string): Promise<EntityCondominium> => {
    const condominiumDoc = doc(condominiumsCollection, id);

    const resource = await getDoc<EntityCondominium>(condominiumDoc);

    const data = resource.data();

    if (!data) {
      throw new FetchError({ trace: 'fetchById', message: `Não foi possível carregar um condominío com o id: ${id}` });
    }

    return data;
  };

  const update = async (condominium: Partial<EntityCondominium> & { id: string }): Promise<void> => {
    const updateCondominium = httpsCallable(functions, 'updateCondominium');

    const instance = new UpdateCondominiumInput({ ...condominium, bondCode: condominium.bondCode?.toUpperCase() });

    await updateCondominium(instanceToPlain(instance));
  };

  const create = async (condominium: Omit<EntityCondominium, 'id'>): Promise<EntityCondominium> => {
    const createCondominium = httpsCallable<unknown, EntityCondominium>(functions, 'insertCondominium');

    const newCondominium = new CreateCondominiumInput({
      ...condominium,
      bondCode: condominium.bondCode?.toUpperCase(),
    });

    const { data } = await createCondominium(instanceToPlain(newCondominium));

    return plainToInstance(EntityCondominium, data);
  };

  return { fetchAll, fetchById, update, create };
}

export default useCondominiums;
